/**
 * A value the app already holds, shown instead of asked for.
 *
 * This is the shape of the owner's rule: "si ya lo introduciste una vez nunca
 * más debes de volver a introducirlo, a menos que sea para editarlo". A datum
 * we have proven is displayed with WHERE IT CAME FROM, and the person confirms
 * it rather than retyping it.
 *
 * It lived inside `profile/verification/compliance.tsx`, which is why exactly
 * one screen in the app honoured the rule while the bank-account form asked a
 * host to type a DUI the national registry had already confirmed.
 *
 * The provenance line is not decoration. A field that appears filled in with no
 * explanation reads as the app having guessed; naming the source is what makes
 * it trustworthy enough not to re-ask.
 */
import { View, Text } from 'react-native'
import { ShieldCheck } from '@/components/ui/icons'
import { COLORS, FONTS, SPACING } from '@/lib/brand'
import type { useSemanticColors } from '@/lib/theme/tokens'

type Colors = ReturnType<typeof useSemanticColors>

interface ConfirmedFieldProps {
  label: string
  value: string
  /** Human-readable provenance, e.g. "De tu DUI verificado". */
  source: string
  testID: string
  colors: Colors
}

/** @public */
export function ConfirmedField({ label, value, source, testID, colors }: ConfirmedFieldProps) {
  return (
    <View
      testID={testID}
      // Read as one unit: three separate nodes would make a screen reader
      // announce the label, the value and the provenance as unrelated
      // fragments.
      accessible
      accessibilityRole="text"
      accessibilityLabel={`${label}: ${value}. ${source}`}
    >
      <Text
        style={{
          fontSize: 14,
          fontFamily: FONTS.body,
          color: colors.textPrimary,
          opacity: 0.75,
          letterSpacing: 0.2,
          marginBottom: SPACING.xs,
        }}
      >
        {label}
      </Text>
      <Text style={{ fontSize: 16, fontFamily: FONTS.body, color: colors.textPrimary }}>
        {value}
      </Text>
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          gap: SPACING.xs,
          marginTop: 2,
        }}
      >
        <ShieldCheck size={13} color={COLORS.coral.DEFAULT} strokeWidth={2} />
        <Text
          style={{
            flex: 1,
            fontSize: 12,
            lineHeight: 16,
            fontFamily: FONTS.body,
            color: colors.textSecondary,
          }}
        >
          {source}
        </Text>
      </View>
    </View>
  )
}
